/**
 * DevExtreme 22 bu projede `getInstance`'ı `devextreme/core/element` üzerinden export etmiyor.
 * Global çift tıklama için son açılan DateBox örneğini onOpened (`injectQuickDatePresets`) ile tutuyoruz.
 */
let lastOpenedDateBoxInstance = null;

const ABSOLUTE_PRESETS = [
    { key: 'today', text: 'Bugün', absoluteDays: 0 },
    { key: 'yesterday', text: 'Dün', absoluteDays: -1 },
    { key: 'tomorrow', text: 'Yarın', absoluteDays: 1 },
];

const RELATIVE_PRESETS = [
    { key: 'nextWeek', text: 'Gelecek Hafta', days: 7 },
    { key: 'nextMonth', text: 'Gelecek Ay', months: 1 },
    { key: 'nextYear', text: 'Gelecek Yıl', years: 1 },
    { key: 'prevWeek', text: 'Geçen Hafta', days: -7 },
    { key: 'prevMonth', text: 'Geçen Ay', months: -1 },
    { key: 'prevYear', text: 'Geçen Yıl', years: -1 },
];

function findOpenedDateBoxInstance() {
    const inst = lastOpenedDateBoxInstance;
    try {
        if (inst && typeof inst.option === 'function' && inst.option('opened')) {
            return inst;
        }
    } catch (_) {
        lastOpenedDateBoxInstance = null;
    }
    return null;
}

function shiftDate(base, preset) {
    const d = new Date(base);
    if (preset.years) d.setFullYear(d.getFullYear() + preset.years);
    if (preset.months) d.setMonth(d.getMonth() + preset.months);
    if (preset.days) d.setDate(d.getDate() + preset.days);
    return d;
}

function applyClock(dayDate, type, currentValue) {
    const next = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
    if (String(type || '').toLowerCase() === 'date') {
        next.setHours(0, 0, 0, 0);
        return next;
    }
    const base = currentValue ? new Date(currentValue) : new Date();
    if (!Number.isNaN(base.getTime())) {
        next.setHours(base.getHours(), base.getMinutes(), base.getSeconds(), 0);
    }
    return next;
}

/** Takvim hücresi data-value biçimi: yyyy/MM/dd */
export function parseCalendarCellDate(raw) {
    const match = /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(String(raw || ''));
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
}

export function resolveQuickPresetDate(preset, { type = 'date', value = null, visibleDay = null, now = new Date() } = {}) {
    if (!preset) return null;
    if (preset.absoluteDays != null) {
        const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() + preset.absoluteDays);
        return applyClock(day, type, value);
    }
    const base = visibleDay ? new Date(visibleDay) : (value ? new Date(value) : new Date(now));
    if (Number.isNaN(base.getTime())) return null;
    return applyClock(shiftDate(base, preset), type, value);
}

function commitDateAndClose(dateBoxInstance, nextDate) {
    if (!dateBoxInstance || !(nextDate instanceof Date) || Number.isNaN(nextDate.getTime())) return false;
    dateBoxInstance.option('value', nextDate);
    if (typeof dateBoxInstance.close === 'function') dateBoxInstance.close();
    else dateBoxInstance.option('opened', false);
    return true;
}

function isDayCell(cell) {
    if (!cell || !cell.classList?.contains('dx-calendar-cell')) return false;
    if (cell.classList.contains('dx-calendar-week-number-cell') || cell.classList.contains('dx-calendar-empty-cell')) return false;
    const table = cell.closest?.('table');
    return !!table?.querySelector('thead');
}

function readVisibleDay(popupContent) {
    const cell = popupContent?.querySelector?.('.dx-calendar-contoured-date, .dx-calendar-selected-date');
    if (!isDayCell(cell)) return null;
    return parseCalendarCellDate(cell.getAttribute('data-value'));
}

function commitCellDate(popupContent, dateBoxInstance, cell) {
    const db = dateBoxInstance || findOpenedDateBoxInstance();
    const day = parseCalendarCellDate(cell?.getAttribute?.('data-value'));
    if (!db || !day) return;
    const next = applyClock(day, db.option('type'), db.option('value'));
    commitDateAndClose(db, next);
}

let lastDoubleClickStamp = 0;

function onCalendarDoubleClick(evt, dateBoxInstance) {
    const cell = evt?.target?.closest?.('.dx-calendar-cell');
    if (!isDayCell(cell)) return;
    const now = Date.now();
    if (now - lastDoubleClickStamp < 40) return;
    lastDoubleClickStamp = now;
    const popupContent = evt.target?.closest?.('.dx-popup-content');
    commitCellDate(popupContent, dateBoxInstance, cell);
}

function bindPopupDoubleClickConfirm(popupContent, dateBoxInstance) {
    if (!popupContent || popupContent.dataset.quickDateDblBound === '1') return;
    popupContent.dataset.quickDateDblBound = '1';
    popupContent.addEventListener(
        'dblclick',
        (evt) => onCalendarDoubleClick(evt, dateBoxInstance),
        true,
    );
}

function ensureGlobalDateDoubleClickConfirm() {
    if (typeof document === 'undefined') return;
    if (document.__dxDateDoubleClickConfirmBound) return;
    document.__dxDateDoubleClickConfirmBound = true;
    document.addEventListener(
        'dblclick',
        (evt) => onCalendarDoubleClick(evt, null),
        true,
    );
}

function makePresetButton(dateBoxInstance, popupContent, preset) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = preset.text;
    btn.className = 'dx-button dx-button-mode-outlined dx-widget';
    btn.style.padding = '3px 8px';
    if (preset.absoluteDays != null) btn.style.flex = '1 1 30%';
    btn.addEventListener('mousedown', (evt) => evt.preventDefault());
    btn.addEventListener('click', (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        const next = resolveQuickPresetDate(preset, {
            type: dateBoxInstance.option('type'),
            value: dateBoxInstance.option('value'),
            visibleDay: preset.absoluteDays == null ? readVisibleDay(popupContent) : null,
        });
        commitDateAndClose(dateBoxInstance, next);
    });
    return btn;
}

export function injectQuickDatePresets(dateBoxInstance) {
    if (!dateBoxInstance || typeof document === 'undefined') return;
    lastOpenedDateBoxInstance = dateBoxInstance;
    ensureGlobalDateDoubleClickConfirm();

    let tries = 0;
    const maxTries = 40;
    const interval = setInterval(() => {
        const popupContents = Array.from(document.querySelectorAll('.dx-dropdowneditor-overlay .dx-popup-content'));
        const popupContent = popupContents.length > 0 ? popupContents[popupContents.length - 1] : null;
        if (popupContent) {
            bindPopupDoubleClickConfirm(popupContent, dateBoxInstance);
            const old = popupContent.querySelector('.quick-date-footer');
            if (old) old.remove();

            const footer = document.createElement('div');
            footer.className = 'quick-date-footer';
            footer.style.display = 'flex';
            footer.style.flexWrap = 'wrap';
            footer.style.gap = '4px';
            footer.style.padding = '6px 2px 2px 2px';
            footer.style.borderTop = '1px solid rgba(0,0,0,0.08)';
            footer.style.marginTop = '6px';

            ABSOLUTE_PRESETS.forEach((preset) => {
                footer.appendChild(makePresetButton(dateBoxInstance, popupContent, preset));
            });
            const breakRow = document.createElement('div');
            breakRow.style.flexBasis = '100%';
            breakRow.style.height = '0';
            footer.appendChild(breakRow);
            RELATIVE_PRESETS.forEach((preset) => {
                footer.appendChild(makePresetButton(dateBoxInstance, popupContent, preset));
            });

            popupContent.appendChild(footer);
            clearInterval(interval);
        }
        tries += 1;
        if (tries > maxTries) clearInterval(interval);
    }, 30);
}

ensureGlobalDateDoubleClickConfirm();
